import { useState, useCallback } from 'react';
import { BatchGiftEntry, BatchGiftRequest } from '../../../types/invitation';
import { giftService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';

const log = createLogger('gift');

interface UseBatchGiftEntryOptions {
  invitationId: string;
  onRefresh: () => Promise<void>;
}

export function useBatchGiftEntry({ invitationId, onRefresh }: UseBatchGiftEntryOptions) {
  const [batchEntries, setBatchEntries] = useState<BatchGiftEntry[]>([]);
  const [loadingBatchData, setLoadingBatchData] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);

  const loadBatchData = useCallback(async () => {
    try {
      setLoadingBatchData(true);
      const entries = await giftService.getRSVPGuestsWithGiftStatus(invitationId);
      setBatchEntries(entries);
    } catch (error) {
      log.error('載入批量資料失敗:', error);
      alert(`載入失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setLoadingBatchData(false);
    }
  }, [invitationId]);

  const updateBatchEntryAmount = useCallback((rsvpId: string, amount: number) => {
    setBatchEntries(prev =>
      prev.map(entry => (entry.rsvpId === rsvpId ? { ...entry, amount } : entry))
    );
  }, []);

  const updateBatchEntryMessage = useCallback((rsvpId: string, message: string) => {
    setBatchEntries(prev =>
      prev.map(entry => (entry.rsvpId === rsvpId ? { ...entry, message } : entry))
    );
  }, []);

  const handleBatchSubmit = useCallback(async () => {
    try {
      setBatchProcessing(true);

      const validEntries = batchEntries.filter(entry => entry.amount > 0);

      if (validEntries.length === 0) {
        alert('請至少填入一筆紅包金額');
        return;
      }

      const request: BatchGiftRequest = {
        invitationId,
        entries: validEntries,
      };

      await giftService.processBatchGiftRecords(request);
      await Promise.all([onRefresh(), loadBatchData()]);

      alert(`成功處理 ${validEntries.length} 筆紅包記錄！`);
    } catch (error) {
      log.error('批量提交失敗:', error);
      alert(`批量提交失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setBatchProcessing(false);
    }
  }, [batchEntries, invitationId, onRefresh, loadBatchData]);

  return {
    batchEntries,
    loadingBatchData,
    batchProcessing,
    loadBatchData,
    updateBatchEntryAmount,
    updateBatchEntryMessage,
    handleBatchSubmit,
  };
}
